'use client'

import type { ButtonHTMLAttributes, ReactNode } from 'react'
import Link from 'next/link'
import { cn } from '../../lib/utils'

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'gold'
type ButtonSize = 'sm' | 'md' | 'lg' | 'icon'

const variantClass: Record<ButtonVariant, string> = {
  primary: 'ui-button-primary',
  secondary: 'ui-button-secondary',
  outline: 'ui-button-outline',
  ghost: 'ui-button-ghost',
  danger: 'ui-button-danger',
  gold: 'ui-button-gold',
}

const sizeClass: Record<ButtonSize, string> = {
  sm: 'ui-button-sm',
  md: 'ui-button-md',
  lg: 'ui-button-lg',
  icon: 'ui-button-icon',
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  leftIcon?: ReactNode
  rightIcon?: ReactNode
}

export function Button({
  children,
  className,
  variant = 'primary',
  size = 'md',
  leftIcon,
  rightIcon,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn('ui-button', variantClass[variant], sizeClass[size], className)}
      {...props}
    >
      {leftIcon}
      {children}
      {rightIcon}
    </button>
  )
}

interface ButtonLinkProps {
  href: string
  children: ReactNode
  className?: string
  variant?: ButtonVariant
  size?: ButtonSize
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  target?: string
}

const PROTECTED_PREFETCH_PREFIXES = [
  '/admin',
  '/dashboard',
  '/moderator',
  '/volunteer/dashboard',
  '/contributor/dashboard',
  '/committee/dashboard',
  '/waqf/evidence',
  '/waqf/affidavits',
  '/waqf/gazette',
]

function shouldDisablePrefetch(href: string) {
  if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return false
  try {
    const pathname = new URL(href, 'https://masjids.in').pathname
    return PROTECTED_PREFETCH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
  } catch {
    return false
  }
}

export function ButtonLink({
  href,
  children,
  className,
  variant = 'primary',
  size = 'md',
  leftIcon,
  rightIcon,
  target,
}: ButtonLinkProps) {
  return (
    <Link
      href={href}
      target={target}
      prefetch={shouldDisablePrefetch(href) ? false : undefined}
      className={cn('ui-button', variantClass[variant], sizeClass[size], className)}
    >
      {leftIcon}
      {children}
      {rightIcon}
    </Link>
  )
}
